/* The tenant this tab chose to act as, held as the tenant token
 * `POST /api/session/tenant` returned (15 section 2.2, app/tenant_token.py).
 *
 * PER TAB, in sessionStorage, and that is the point of it: a choice made in
 * one tab never moves another tab into a different customer mid-task (02 ->
 * The tenant a caller acts as travels in a token we sign). Read through
 * lib/storage.ts, so unavailable storage is no choice rather than a crash.
 *
 * What it deliberately is not: authorization. The API reads the membership on
 * every request whatever this holds; a stale or revoked choice is refused
 * there and dropped here (api/me.ts).
 */
import { readStored, writeStored } from "./storage";

const KEY = "arbor.tenant-choice";

export interface TenantChoice {
  tenant_token: string;
  tenant_id: string;
  expires_at: string;
}

/* The stored choice, or null where it is absent, unreadable or expired. A
 * token the API would refuse as expired is not sent, so the shell asks for
 * a choice again instead of meeting a 403. */
export function parseChoice(raw: string | null, now: number): TenantChoice | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TenantChoice>;
    if (
      typeof parsed.tenant_token !== "string" ||
      typeof parsed.tenant_id !== "string" ||
      typeof parsed.expires_at !== "string"
    ) {
      return null;
    }
    const expires = Date.parse(parsed.expires_at);
    if (!Number.isFinite(expires) || expires <= now) return null;
    return {
      tenant_token: parsed.tenant_token,
      tenant_id: parsed.tenant_id,
      expires_at: parsed.expires_at,
    };
  } catch {
    return null;
  }
}

export function chosenTenant(now: number = Date.now()): TenantChoice | null {
  return parseChoice(readStored("session", KEY), now);
}

export function rememberChoice(choice: TenantChoice): void {
  writeStored("session", KEY, JSON.stringify(choice));
}

export function forgetChoice(): void {
  writeStored("session", KEY, null);
}

/* Whether `/api/me`, read with `choice` sent, says the choice no longer holds.
 * A token the API cannot read is a 403; one naming a tenant the caller can no
 * longer reach - revoked since, or support access removed - is a 200 whose
 * active tenant is not the chosen one (15 section 3.11). Either way the
 * choice is dropped, or every request after sends a tenant the gates refuse
 * and the shell, offered nothing to choose, never asks again. */
export function choiceLapsed(choice: TenantChoice, me: { active_tenant_id: string | null }): boolean {
  return me.active_tenant_id !== choice.tenant_id;
}

/* Requests refused while this tab sent a choice, for the shell to read
 * `/api/me` again on (api/me.ts). A 403 with a choice sent is either that
 * choice lapsing - revoked since, or support access removed - or an ordinary
 * refusal in a tenant the caller still holds, and only `/api/me` can say
 * which (`choiceLapsed`), so every one is reported. */
type RefusalListener = () => void;
const refusalListeners = new Set<RefusalListener>();

export function onChoiceRefused(listener: RefusalListener): () => void {
  refusalListeners.add(listener);
  return () => {
    refusalListeners.delete(listener);
  };
}

export function reportRefusal(status: number, sentChoice: boolean): void {
  if (!sentChoice || status !== 403) return;
  for (const listener of refusalListeners) listener();
}

interface Offered {
  tenants: { name: string; via_support: boolean }[];
  active_tenant_id: string | null;
}

/* Whether the shell must ask for a tenant before it can show anything: more
 * than one is offered and none is active. With one, the API acts as it
 * without a choice (app/tenancy.py), so there is nothing to ask. */
export function needsChoice(me: Offered): boolean {
  return me.tenants.length > 1 && me.active_tenant_id === null;
}

/* A tenant as the picker names it. A support tenant says so, and says it is
 * read-only, so nobody takes a customer's screen for one they may change. A
 * field-audit tenant says what it may change. */
export function tenantLabel(tenant: { name: string; via_support: boolean; via_field_audit: boolean }): string {
  if (tenant.via_field_audit) return `${tenant.name} · field audits only`;
  return tenant.via_support ? `${tenant.name} · support, read only` : tenant.name;
}
